import { ReactNode, useEffect, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { CCMSProvider, AuthProvider, useAuth, useHttpClient } from '@actschurch/ccms-sdk';
import { BrowserRouter } from 'react-router-dom';
import { theme, cssVariablesResolver } from '@/theme/theme';
import { carpoolColorSchemeManager } from '@/theme/colorScheme';
import { CCMS_BASE_URL, CCMS_SERVICE_PATH_RIDES } from '@/config/ccms';
import { createSecureStorage } from '@/utils/secureStorage';
import { useDeviceRegistration } from '@/hooks/useDeviceRegistration';

/** Appearance choice (Match my phone / Light / Dark), persisted in localStorage inside try/catch. */
const colorSchemeManager = carpoolColorSchemeManager();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
      staleTime: 60 * 1000,
    },
  },
});

/**
 * Wraps the SDK <c>AuthProvider</c>. Has to live inside <c>CCMSProvider</c> so it
 * can read the <c>HttpClient</c>. Mirrors the ccms_v2 / Resource Centre pattern.
 */
function AppWithAuth({ children }: { children: ReactNode }) {
  const httpClient = useHttpClient();
  const storage = useMemo(() => createSecureStorage(), []);
  return (
    <AuthProvider
      httpClient={httpClient}
      storage={storage}
      autoRefresh
      refreshThresholdSeconds={60}
      onError={(err) => console.error('[Auth] error', err)}
    >
      {children}
    </AuthProvider>
  );
}

/**
 * Registers this browser as a web push device once the user is signed in
 * (non-blocking, silent on failure, skipped when Firebase isn't configured).
 * Same pattern as frontend/ccms_skeleton/src/main.tsx.
 */
function AppWithDeviceRegistration({ children }: { children: ReactNode }) {
  const { state: authState } = useAuth();
  const deviceRegistration = useDeviceRegistration();

  useEffect(() => {
    if (authState.isAuthenticated && authState.user) {
      deviceRegistration.autoRegisterOnLogin();
    } else {
      deviceRegistration.resetRegistration();
    }
  }, [authState.isAuthenticated, authState.user, deviceRegistration]);

  return <>{children}</>;
}

/**
 * Root providers for the Rides portal.
 * - <c>CCMSProvider</c> wires the HttpClient + the SDK services we use
 *   (users for auth + the account photo, members for the profile gate, file for
 *   the photo upload, rides for everything else).
 * - <c>AppWithAuth</c> wires the SDK's <c>AuthProvider</c> + secure storage.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <CCMSProvider
          config={{ baseUrl: CCMS_BASE_URL }}
          services={{
            users: { servicePath: 'users' },
            members: { servicePath: 'members' },
            // FileManagement — profile-photo upload (presign → S3 PUT → confirm). The SDK key is `file`.
            file: { servicePath: 'files' },
            rides: { servicePath: CCMS_SERVICE_PATH_RIDES },
          }}
        >
          <AppWithAuth>
            <AppWithDeviceRegistration>
              <MantineProvider theme={theme} defaultColorScheme="auto" colorSchemeManager={colorSchemeManager} cssVariablesResolver={cssVariablesResolver}>
                <ModalsProvider>{children}</ModalsProvider>
              </MantineProvider>
            </AppWithDeviceRegistration>
          </AppWithAuth>
        </CCMSProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
